import {
  DndContext,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type MouseEventHandler } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useMixpanel } from '../../../../contexts';
import { MixpanelEvent, MixpanelEventProperty, MixpanelEventSource } from '../../../../tokens';
import { ButtonVariants, IconButton } from '../../../Button';
import { Box } from '../../../Core';
import { Icon, IconName } from '../../../Icons';
import { IndicatorBadge, IndicatorBadgeSizes, IndicatorBadgeVariants } from '../../../IndicatorBadge';
import { Text } from '../../../Text';
import { useMobileModal } from '../../MobileModalSheet/MobileModalProvider';
import { getEffectiveNavigationPath, type TabBarItem } from '../types';
import { OverflowDndArea, OverflowDragHandle, OverflowModalWrapper, OverflowRow, OverflowRowButton } from './styles';

interface OverflowModalProps<T extends TabBarItem> {
  items: T[];
  onItemsChange: (items: T[]) => void;
  onNavigate: (path: string) => void;
  onAddTab?: () => void;
  onOpenItemActions?: (item: T) => void;
  onOpenDeleteConfirm?: (item: T, onConfirmDelete: () => void) => void;
}

export function OverflowModal<T extends TabBarItem>({
  items,
  onItemsChange,
  onNavigate,
  onAddTab,
  onOpenItemActions,
  onOpenDeleteConfirm,
}: OverflowModalProps<T>) {
  const { closeModal } = useMobileModal();
  const mixpanel = useMixpanel(MixpanelEventSource.MobileTabBar);
  const { pathname } = useLocation();
  const [orderedItems, setOrderedItems] = useState(items);

  useEffect(() => {
    setOrderedItems(items);
  }, [items]);

  const itemIds = useMemo(() => orderedItems.map(getNavItemKey), [orderedItems]);

  const handleConfirmDeleteItem = useCallback(
    (item: T) => {
      const nextItems = orderedItems.filter(orderedItem => getNavItemKey(orderedItem) !== getNavItemKey(item));
      setOrderedItems(nextItems);
      onItemsChange(nextItems);
    },
    [onItemsChange, orderedItems]
  );

  // Only the most specific (longest path) matching item should be highlighted.
  // Multiple items may share a common prefix with the current route (e.g. a parent
  // panel path matches every child route), so we pick the one whose path is the
  // longest prefix of `pathname`.
  // We want to support the same routes as desktop but the content structure is different
  // between the Market tab and the blotters.
  const selectedItemId = useMemo(() => {
    let bestId: string | undefined;
    let bestPathLength = -1;
    for (const item of orderedItems) {
      if (item.path == null) {
        continue;
      }
      if (matchPath({ path: item.path, end: false }, pathname) == null) {
        continue;
      }
      if (item.path.length > bestPathLength) {
        bestPathLength = item.path.length;
        bestId = getNavItemKey(item);
      }
    }
    return bestId;
  }, [orderedItems, pathname]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } })
  );

  const handlePressItem = useCallback(
    (item: TabBarItem) => {
      const effectivePath = getEffectiveNavigationPath(item);
      if (effectivePath == null) {
        return;
      }

      mixpanel.track(MixpanelEvent.NavigateTo, { [MixpanelEventProperty.Path]: effectivePath });
      if (item.mixpanelEvent) {
        mixpanel.track(item.mixpanelEvent);
      }
      onNavigate(effectivePath);
      closeModal();
    },
    [closeModal, mixpanel, onNavigate]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      if (over == null || active.id === over.id) {
        return;
      }

      setOrderedItems(prev => {
        const oldIndex = prev.findIndex(item => getNavItemKey(item) === active.id);
        const newIndex = prev.findIndex(item => getNavItemKey(item) === over.id);
        if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) {
          return prev;
        }

        mixpanel.track(MixpanelEvent.ReorderMobileTabBarItems, {
          [MixpanelEventProperty.FromIndex]: oldIndex,
          [MixpanelEventProperty.ToIndex]: newIndex,
        });

        const nextItems = arrayMove(prev, oldIndex, newIndex);
        onItemsChange(nextItems);
        return nextItems;
      });
    },
    [mixpanel, onItemsChange]
  );

  return (
    <OverflowModalWrapper>
      <OverflowDndArea>
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
            {orderedItems.map(item => (
              <SortableOverflowItem
                key={getNavItemKey(item)}
                item={item}
                isSelected={getNavItemKey(item) === selectedItemId}
                canRemoveItem={orderedItems.length > 1}
                onPress={handlePressItem}
                onOpenActions={() => onOpenItemActions?.(item)}
                onOpenDeleteConfirm={() => onOpenDeleteConfirm?.(item, () => handleConfirmDeleteItem(item))}
              />
            ))}
            {/* Scroll buffer at the bottom */}
            <Box h={16} flex="0 0 auto" />
          </SortableContext>
        </DndContext>
        {onAddTab != null && (
          <OverflowRow>
            <OverflowRowButton type="button" onClick={onAddTab} data-testid="mobile-sub-tab-add-button">
              <Icon size={20} icon={IconName.Plus} />
              <Text
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                flex="1 1 0"
                color="colorTextImportant"
              >
                New tab
              </Text>
            </OverflowRowButton>
          </OverflowRow>
        )}
      </OverflowDndArea>
    </OverflowModalWrapper>
  );
}

function SortableOverflowItem({
  item,
  isSelected,
  canRemoveItem,
  onPress,
  onOpenActions,
  onOpenDeleteConfirm,
}: {
  item: TabBarItem;
  isSelected: boolean;
  canRemoveItem: boolean;
  onPress: (item: TabBarItem) => void;
  onOpenActions?: () => void;
  onOpenDeleteConfirm?: () => void;
}) {
  const id = getNavItemKey(item);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 1 : undefined,
  };
  const showIndicator = item.indicatorCount != null && item.indicatorCount > 0;
  const showEditButton = item.isEditable === true && onOpenActions != null;
  const showDeleteButton = canRemoveItem && item.isRemovable === true && onOpenDeleteConfirm != null;
  const handleContextMenu = useCallback<MouseEventHandler>(
    event => {
      event.preventDefault();
      onOpenActions?.();
    },
    [onOpenActions]
  );
  const handleOpenActions = useCallback<MouseEventHandler>(
    event => {
      event.stopPropagation();
      onOpenActions?.();
    },
    [onOpenActions]
  );
  const handleOpenDeleteConfirm = useCallback<MouseEventHandler>(
    event => {
      event.stopPropagation();
      onOpenDeleteConfirm?.();
    },
    [onOpenDeleteConfirm]
  );

  return (
    <OverflowRow ref={setNodeRef} style={style} isSelected={isSelected} aria-current={isSelected ? 'page' : undefined}>
      <OverflowRowButton
        type="button"
        onClick={() => onPress(item)}
        onContextMenu={handleContextMenu}
        data-testid={`mobile-tab-overflow-item-${id}`}
      >
        <Icon size={20} icon={item.icon ?? IconName.Table} />
        <Text overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" flex="1 1 0" color="colorTextImportant">
          {item.label}
        </Text>
        {showIndicator && (
          <IndicatorBadge
            size={IndicatorBadgeSizes.Small}
            variant={IndicatorBadgeVariants.Notification}
            data-testid={getOverflowIndicatorTestId(item)}
          >
            {item.indicatorCount}
          </IndicatorBadge>
        )}
      </OverflowRowButton>
      {showEditButton && (
        <IconButton
          ghost
          icon={IconName.Pencil}
          aria-label={`Edit ${item.label ?? 'tab'}`}
          data-testid={`mobile-tab-overflow-edit-button-${id}`}
          onClick={handleOpenActions}
        />
      )}
      {showDeleteButton && (
        <IconButton
          ghost
          variant={ButtonVariants.Negative}
          icon={IconName.Trash}
          aria-label={`Delete ${item.label ?? 'tab'}`}
          data-testid={`mobile-tab-overflow-delete-button-${id}`}
          onClick={handleOpenDeleteConfirm}
        />
      )}
      <OverflowDragHandle data-testid="mobile-tab-overflow-drag-handle" {...attributes} {...listeners}>
        <Icon size={20} icon={IconName.DragVertical} />
      </OverflowDragHandle>
    </OverflowRow>
  );
}

function getNavItemKey<T extends TabBarItem>(item: T) {
  return item.id ?? item.path ?? '';
}

function getOverflowIndicatorTestId(item: TabBarItem) {
  return `mobile-tab-overflow-indicator-${getNavItemKey(item)}`;
}
